import { useAppDispatch } from '@hooks/useDispatch';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { CommonCellProps } from '../Cell';
import classes from './CellMultiNumber.module.css';
import { useTranslation } from 'react-i18next';

export default function CellMultiNumber({ rowId, columnId, value, currentRow, isEditing }: CommonCellProps ) { const dispatch = useAppDispatch();
  const { t } = useTranslation( 'grid' );
  const parseToTokens = useCallback(( val: any ) => {
    if ( typeof val === 'string' ) {
      return val
        .split( ',' )
        .map(( t ) => t.trim())
        .filter( Boolean );
    } else if ( Array.isArray( val )) {
      return val.map(( x ) => String( x ));
    }
    return [];
  }, []);

  const [ tokens, setTokens ] = useState<string[]>([]);
  const [ inputValue, setInputValue ] = useState( '' );

  useEffect(() => {
    setTokens( parseToTokens( value ));
  }, [ value, parseToTokens ]);

  const commitEdit = useCallback(( finalTokens: string[]) => {
      if ( !currentRow ) return;
      const newValue = finalTokens.join( ', ' );
      const newValues = { ...currentRow.values, [columnId]: newValue };

      dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
      dispatch(
        cellValueChanged({
          rowId,
          columnId,
          oldValue: currentRow.values[columnId],
          newValue
        })
      );
      dispatch( setEditingCell( null ));
    },
    [ dispatch, rowId, columnId, currentRow ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);

  const handleKeyDown = useCallback(
    ( e: KeyboardEvent<HTMLInputElement> ) => {
      if ( !isEditing ) return;
      if ( e.key === 'Enter' || e.key === ',' ) {
        e.preventDefault();
        const trimmed = inputValue.trim();
        const parsed = parseFloat( trimmed );
        if ( !Number.isNaN( parsed )) {
          setTokens(( prev ) => [ ...prev, trimmed ]);
        }
        setInputValue( '' );
      } else if ( e.key === 'Escape' ) {
        e.preventDefault();
        cancelEdit();
      }
    },
    [ isEditing, inputValue, cancelEdit ]
  );

  const handleBlur = useCallback(() => {
    commitEdit( tokens );
  }, [ tokens, commitEdit ]);

  const handleRemoveToken = useCallback(( index: number ) => {
    setTokens(( prev ) => prev.filter(( _, i ) => i !== index ));
  }, []);


  if ( !isEditing ) {
    return (
      <span className={classes.cellMultiStringWrapper}>
        {tokens.map(( token, idx ) => (
          <span key={idx} className={classes.cellMultiStringPill}>
            {token}
          </span>
        ))}
      </span>
    );
  }

 
  return (
    <div className={classes.cellMultiStringEdit}>
      {/* Input row */}
      <div className={classes.cellMultiStringEditInputLine}>
        <input
          className={classes.cellMultiStringEditInput}
          placeholder={t( 'cell_multi_number_placeholder' )}
          value={inputValue}
          onChange={( e ) => setInputValue( e.currentTarget.value )}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoFocus
        />
      </div>

      {/* Pills row */}
      <div className={classes.cellMultiStringEditTokensLine}>
        {tokens.map(( token, idx ) => (
          <span key={idx} className={classes.cellMultiStringEditPill}>
            {token}
            <span
              className={classes.cellMultiStringEditPillRemoveBtn}
              onMouseDown={( e ) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveToken( idx );
              }}
            >
              &times;
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
